import { z } from 'zod';
import { BiasType } from 'libs/end-to-end/src/bias-type';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { ImpactFactor } from 'libs/end-to-end/src/impact-factor';

/**
 * This exists so we don't have to do a data migration for the treatment types since they've changed fom their original versions.
 * They'll still exist with the legacy names in the database (the values here), but we'll use these new names (the keys here) in the UI.
 * That way we only have this one place where the names are mapped.
 */
export enum TreatmentTypeNativeEnum {
  DiversityCopilot = 'AIBasedAlgorithm',
  ManagerBestPractices = 'ManagerComparativeAdvantage',
  IntelligentSourcingStrategy = 'TalentSourcing',
  TrainingStrategyEmpathySkills = 'EmpathyTraining',
  ManagerUpskilling = 'SuboptimalDecisionMakers',
  TrainingStrategyCounterStereotype = 'CounterStereotypeTraining',
  TrainingStrategyStructuralInequality = 'StructuralInequalityTraining',
  SuccessPhenotype = 'PerformanceDecomposition',
  /**
   * @deprecated
   */
  ProvideMoreData = 'ProvideMoreData',
  CompensationCopilot = 'CompensationCopilot',
  HiringCopilot = 'HiringCopilot',
  PromotionCopilot = 'PromotionCopilot',
  SourcingCopilot = 'SourcingCopilot',
  HiringProcessOptimization = 'HiringProcessOptimization',
  PerformanceProcessOptimization = 'PerformanceProcessOptimization',
  PromotionProcessOptimization = 'PromotionProcessOptimization',
  /**
   * @deprecated
   */
  deprecated_InterGroupContact = 'InterGroupContact',
  /**
   * @deprecated
   */
  deprecated_InterGroupContactTraining = 'InterGroupContactTraining',
  /**
   * @deprecated
   */
  deprecated_BestPracticesMakingAlgorithms = 'BestPracticesMakingAlgorithms',
}

export const TreatmentType = z.nativeEnum(TreatmentTypeNativeEnum);

export type TreatmentType = z.infer<typeof TreatmentType>;

export const TreatmentTypeIds: Record<TreatmentType, number> = {
  [TreatmentTypeNativeEnum.DiversityCopilot]: 1,
  [TreatmentTypeNativeEnum.ManagerBestPractices]: 2,
  [TreatmentTypeNativeEnum.IntelligentSourcingStrategy]: 3,
  [TreatmentTypeNativeEnum.deprecated_InterGroupContact]: 4,
  [TreatmentTypeNativeEnum.TrainingStrategyEmpathySkills]: 5,
  [TreatmentTypeNativeEnum.ManagerUpskilling]: 6,
  [TreatmentTypeNativeEnum.TrainingStrategyCounterStereotype]: 7,
  [TreatmentTypeNativeEnum.deprecated_BestPracticesMakingAlgorithms]: 8,
  [TreatmentTypeNativeEnum.TrainingStrategyStructuralInequality]: 9,
  [TreatmentTypeNativeEnum.SuccessPhenotype]: 10,
  [TreatmentTypeNativeEnum.deprecated_InterGroupContactTraining]: 11,
  [TreatmentTypeNativeEnum.ProvideMoreData]: 12,
  [TreatmentTypeNativeEnum.CompensationCopilot]: 13,
  [TreatmentTypeNativeEnum.HiringCopilot]: 14,
  [TreatmentTypeNativeEnum.PromotionCopilot]: 15,
  [TreatmentTypeNativeEnum.SourcingCopilot]: 16,
  [TreatmentTypeNativeEnum.HiringProcessOptimization]: 17,
  [TreatmentTypeNativeEnum.PerformanceProcessOptimization]: 18,
  [TreatmentTypeNativeEnum.PromotionProcessOptimization]: 19,
};

export const TreatmentStatus = z
  .enum(['Available', 'Selected', 'Active'])
  .describe('TreatmentStatus');
export type TreatmentStatus = z.infer<typeof TreatmentStatus>;

export const TreatmentRecommendation = z
  .enum(['BiggestImpact', 'QuickestWin', 'MostPopular'])
  .describe('TreatmentRecommendation');

export type TreatmentRecommendation = z.infer<typeof TreatmentRecommendation>;

export const ProjectedSavings = z.object({
  min: z.number().int(),
  max: z.number().int(),
  reason: z.string(),
});

export type ProjectedSavings = z.infer<typeof ProjectedSavings>;

export const Treatment = z.object({
  type: TreatmentType,
  focusArea: FocusArea,
  biasType: BiasType,
  dimension: z.string(),
  metric: z.string(),
  description: z.string(),
  level: z.string(),
  status: TreatmentStatus,
  impactFactor: ImpactFactor,
  recommendations: z.array(TreatmentRecommendation),
  projectedSavings: ProjectedSavings.optional(),
});

export type Treatment = z.infer<typeof Treatment>;

export const Treatments = z.object({
  treatments: z.array(Treatment),
});

export type Treatments = z.infer<typeof Treatments>;

export const UpdateTreatments = z.object({
  treatments: z.array(Treatment),
});

export type UpdateTreatments = z.infer<typeof UpdateTreatments>;

export interface CreateTreatmentKeyProps {
  type: TreatmentType;
  focusArea: FocusArea;
  biasType: BiasType;
  dimension: string;
  level: string;
}

export const createTreatmentKey = (props: CreateTreatmentKeyProps): string => {
  return `${props.type}-${props.focusArea}-${props.biasType}-${props.dimension}-${props.level}`;
};

export const isTreatmentStatusSelected = (status: TreatmentStatus): boolean => {
  return status === 'Selected' || status === 'Active';
};
